import { HubConnection, HubConnectionBuilder, LogLevel, HttpTransportType } from '@microsoft/signalr';
import { getLocalInfo } from 'src/utils/encrypt';

export interface SignalRConnectionState {
  isConnected: boolean;
  isConnecting: boolean;
  isReconnecting: boolean;
  connectionId?: string;
}

export class SignalRService {
  private connection: HubConnection | null = null;
  private baseUrl: string;
  private connectionStateHandlers: ((state: SignalRConnectionState) => void)[] = [];
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 2000;
  private isManuallyReconnecting = false;
  private reconnectTimeoutId: NodeJS.Timeout | null = null;
  
  private removeFromWaitingListCallbacks: ((data: { doctorId: number; consultationId: number; status: string }) => void)[] = [];
  private removeFromConsultedListCallbacks: ((data: { doctorId: number; consultationId: number; status: string }) => void)[] = [];
  private removeFromScheduledListCallbacks: ((data: { doctorId: number; consultationId: number; status: string }) => void)[] = [];
  private addToWaitingListCallbacks: ((patientDataList: any[]) => void)[] = [];
  private addToScheduleListCallbacks: ((patientDataList: any[]) => void)[] = [];
  private userJoinedCallbacks: ((userData: any) => void)[] = [];
  
  private handlersRegistered = false;

  constructor() {
    this.baseUrl = import.meta.env.VITE_SIGNALR_URL || import.meta.env.VITE_BASE_URL || 'wss://localhost:44333';
  }

  public async connect(): Promise<void> {
    try {
      const accessToken = getLocalInfo('accessToken');
      
      if (!accessToken) {
        console.warn('No access token found, cannot connect to SignalR');
        return;
      }

      if (this.reconnectTimeoutId) {
        clearTimeout(this.reconnectTimeoutId);
        this.reconnectTimeoutId = null;
      }
      this.isManuallyReconnecting = false;

      if (this.connection && this.connection.state === 'Connected') {
        return;
      }

      if (this.connection) {
        try {
          this.connection.off('onreconnecting');
          this.connection.off('onreconnected');
          this.connection.off('onclose');
          await this.connection.stop().catch(() => {
          });
        } catch (error) {
          console.warn('Error stopping existing connection:', error);
        }
        this.connection = null;
      }

      this.connection = new HubConnectionBuilder()
        .withUrl(`${this.baseUrl}/consultationhub`, {
          transport: HttpTransportType.WebSockets,
          skipNegotiation: true,
          accessTokenFactory: () => {
            const token = getLocalInfo('accessToken');
            if (!token) {
              throw new Error('Access token not available');
            }
            return token;
          },
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            if (retryContext.previousRetryCount === 0) {
              return 0;
            }
            const delay = Math.min(
              this.reconnectDelay * Math.pow(2, retryContext.previousRetryCount - 1),
              30000
            );
            if (retryContext.previousRetryCount < this.maxReconnectAttempts) {
              return delay;
            }
            return null;
          },
        })
        .configureLogging(LogLevel.Warning)
        .build();

      this.setupConnectionHandlers();
      await this.startConnection();
    } catch (error) {
      console.error('Failed to connect to SignalR:', error);
      this.handleConnectionError(error);
    }
  }

  private setupConnectionHandlers(): void {
    if (!this.connection) return;

    this.connection.onreconnecting((error) => {
      this.updateConnectionState({
        isConnected: false,
        isConnecting: false,
        isReconnecting: true,
      });
    });

    this.connection.onreconnected((connectionId) => {
      this.reconnectAttempts = 0;
      this.isManuallyReconnecting = false;
      this.updateConnectionState({
        isConnected: true,
        isConnecting: false,
        isReconnecting: false,
        connectionId,
      });
      this.reregisterEventHandlers();
    });

    this.connection.onclose((error) => {
      console.warn('SignalR connection closed', error);
      this.updateConnectionState({
        isConnected: false,
        isConnecting: false,
        isReconnecting: false,
      });
      
      this.reconnectAttempts = 0;
      
      if (!this.isManuallyReconnecting) {
        this.isManuallyReconnecting = true;
        this.reconnectTimeoutId = setTimeout(() => {
          this.attemptReconnection();
        }, this.reconnectDelay);
      }
    });
  }

  private async attemptReconnection(): Promise<void> {
    if (this.connection) {
      const state = this.connection.state;
      if (state === 'Connected') {
        this.isManuallyReconnecting = false;
        this.reconnectAttempts = 0;
        return;
      }

      if (state === 'Disconnected') {
        try {
          await this.connection.stop().catch(() => {
          });
          this.connection = null;
        } catch (error) {
          console.warn('Error cleaning up disconnected connection:', error);
          this.connection = null;
        }
      }
    }

    if (!this.connection) {
      try {
        await this.connect();
        this.isManuallyReconnecting = false;
        return;
      } catch (error) {
        console.error('Failed to recreate SignalR connection:', error);
        this.isManuallyReconnecting = false;
        this.reconnectAttempts++;
        
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          const delay = this.reconnectDelay * Math.pow(2, Math.min(this.reconnectAttempts - 1, 5));
          this.reconnectTimeoutId = setTimeout(() => {
            this.isManuallyReconnecting = true;
            this.attemptReconnection();
          }, delay);
        } else {
          console.error('SignalR: Max reconnection attempts reached');
        }
        return;
      }
    }

    try {
      await this.connection.start();
      this.reconnectAttempts = 0;
      this.isManuallyReconnecting = false;
    } catch (error) {
      console.error('Manual reconnection failed:', error);
      this.isManuallyReconnecting = false;
      this.reconnectAttempts++;
      
      if (this.connection) {
        try {
          await this.connection.stop().catch(() => {});
        } catch (e) {
        }
        this.connection = null;
      }
      
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        const delay = this.reconnectDelay * Math.pow(2, Math.min(this.reconnectAttempts - 1, 5));
        this.reconnectTimeoutId = setTimeout(() => {
          this.isManuallyReconnecting = true;
          this.attemptReconnection();
        }, delay);
      } else {
        console.error('SignalR: Max reconnection attempts reached');
      }
    }
  }

  private handleConnectionError(error: any): void {
    console.error('SignalR connection error:', error);
    
    const errorMessage = error?.message || '';
    if (errorMessage.includes('timeout') || errorMessage.includes('Server timeout')) {
      console.warn('SignalR timeout detected, attempting reconnection...');
      if (!this.isManuallyReconnecting && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.isManuallyReconnecting = true;
        this.reconnectTimeoutId = setTimeout(() => {
          this.attemptReconnection();
        }, this.reconnectDelay);
      }
    } else if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
      console.warn('SignalR authentication error - token may have expired');
      this.updateConnectionState({
        isConnected: false,
        isConnecting: false,
        isReconnecting: false,
      });
    } else {
      if (!this.isManuallyReconnecting && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.isManuallyReconnecting = true;
        this.reconnectTimeoutId = setTimeout(() => {
          this.attemptReconnection();
        }, this.reconnectDelay);
      }
    }
  }

  private async startConnection(): Promise<void> {
    if (!this.connection) return;

    try {
      this.updateConnectionState({
        isConnected: false,
        isConnecting: true,
        isReconnecting: false,
      });

      await this.connection.start();
      this.reconnectAttempts = 0;
      
      this.updateConnectionState({
        isConnected: true,
        isConnecting: false,
        isReconnecting: false,
        connectionId: this.connection.connectionId || undefined,
      });
      
      this.registerAllHandlers();
    } catch (error) {
      console.error('Failed to start SignalR connection:', error);
      this.handleConnectionError(error);
      this.updateConnectionState({
        isConnected: false,
        isConnecting: false,
        isReconnecting: false,
      });
    }
  }

  public onConnectionStateChange(handler: (state: SignalRConnectionState) => void): void {
    this.connectionStateHandlers.push(handler);
  }

  public offConnectionStateChange(handler: (state: SignalRConnectionState) => void): void {
    const index = this.connectionStateHandlers.indexOf(handler);
    if (index > -1) {
      this.connectionStateHandlers.splice(index, 1);
    }
  }

  private updateConnectionState(state: SignalRConnectionState): void {
    this.connectionStateHandlers.forEach(handler => handler(state));
  }

  public getConnectionState(): SignalRConnectionState {
    if (!this.connection) {
      return {
        isConnected: false,
        isConnecting: false,
        isReconnecting: false,
      };
    }

    return {
      isConnected: this.connection.state === 'Connected',
      isConnecting: this.connection.state === 'Connecting',
      isReconnecting: this.connection.state === 'Reconnecting',
      connectionId: this.connection.connectionId || undefined,
    };
  }

  public async joinGroups(groups: string[]): Promise<void> {
    if (!this.connection || this.connection.state !== 'Connected') {
      console.warn('Cannot join groups: connection not established');
      return;
    }

    try {
      for (const group of groups) {
        await this.connection.invoke('JoinGroup', group);
      }
    } catch (error) {
      console.error('Failed to join groups:', error);
      throw error;
    }
  }

  public async leaveGroups(groups: string[]): Promise<void> {
    if (!this.connection || this.connection.state !== 'Connected') {
      console.warn('Cannot leave groups: connection not established');
      return;
    }

    try {
      for (const group of groups) {
        await this.connection.invoke('LeaveGroup', group);
      }
    } catch (error) {
      console.error('Failed to leave groups:', error);
      throw error;
    }
  }

  public async disconnect(): Promise<void> {
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
    this.isManuallyReconnecting = false;

    if (this.connection) {
      try {
        this.updateConnectionState({
          isConnected: false,
          isConnecting: false,
          isReconnecting: false,
        });

        this.connection.off('onreconnecting');
        this.connection.off('onreconnected');
        this.connection.off('onclose');

        await this.connection.stop();
      } catch (error) {
        console.error('Error stopping SignalR connection:', error);
      } finally {
        this.connection = null;
        this.reconnectAttempts = 0;
        this.handlersRegistered = false;
      }
    }
  }

  public isConnected(): boolean {
    return this.connection?.state === 'Connected';
  }

  public onRemoveFromWaitingList(callback: (data: { doctorId: number; consultationId: number; status: string }) => void): void {
    if (!this.removeFromWaitingListCallbacks.includes(callback)) {
      this.removeFromWaitingListCallbacks.push(callback);
    }
    
    if (this.connection && this.connection.state === 'Connected') {
      this.registerRemoveFromWaitingListHandler(callback);
    }
  }

  private registerRemoveFromWaitingListHandler(callback: (data: { doctorId: number; consultationId: number; status: string }) => void): void {
    if (!this.connection || this.removeFromWaitingListCallbacks.length === 0) return;

    this.connection.off('removeFromWaitingList');
    this.connection.on('removeFromWaitingList', (data: { doctorId: number; consultationId: number; status: string }) => {
      this.removeFromWaitingListCallbacks.forEach(cb => {
        try {
          cb(data);
        } catch (error) {
          console.error('Error in removeFromWaitingList callback:', error);
        }
      });
    });
  }

  public onRemoveFromConsultedList(callback: (data: { doctorId: number; consultationId: number; status: string }) => void): void {
    if (!this.removeFromConsultedListCallbacks.includes(callback)) {
      this.removeFromConsultedListCallbacks.push(callback);
    }
    
    if (this.connection && this.connection.state === 'Connected') {
      this.registerRemoveFromConsultedListHandler(callback);
    }
  }

  private registerRemoveFromConsultedListHandler(callback: (data: { doctorId: number; consultationId: number; status: string }) => void): void {
    if (!this.connection || this.removeFromConsultedListCallbacks.length === 0) return;

    this.connection.off('RemoveFromConsultedList');
    this.connection.on('RemoveFromConsultedList', (data: { doctorId: number; consultationId: number; status: string }) => {
      this.removeFromConsultedListCallbacks.forEach(cb => {
        try {
          cb(data);
        } catch (error) {
          console.error('Error in removeFromConsultedList callback:', error);
        }
      });
    });
  }

  public onRemoveFromScheduledList(callback: (data: { doctorId: number; consultationId: number; status: string }) => void): void {
    if (!this.removeFromScheduledListCallbacks.includes(callback)) {
      this.removeFromScheduledListCallbacks.push(callback);
    }
    
    if (this.connection && this.connection.state === 'Connected') {
      this.registerRemoveFromScheduledListHandler(callback);
    }
  }

  private registerRemoveFromScheduledListHandler(callback: (data: { doctorId: number; consultationId: number; status: string }) => void): void {
    if (!this.connection || this.removeFromScheduledListCallbacks.length === 0) return;

    this.connection.off('removeFromScheduledList');
    this.connection.on('removeFromScheduledList', (data: { doctorId: number; consultationId: number; status: string }) => {
      this.removeFromScheduledListCallbacks.forEach(cb => {
        try {
          cb(data);
        } catch (error) {
          console.error('Error in removeFromScheduledList callback:', error);
        }
      });
    });
  }

  public onAddToWaitingList(callback: (patientDataList: any[]) => void): void {
    if (!this.addToWaitingListCallbacks.includes(callback)) {
      this.addToWaitingListCallbacks.push(callback);
    }
    
    if (this.connection && this.connection.state === 'Connected') {
      this.registerAddToWaitingListHandler(callback);
    }
  }

  private registerAddToWaitingListHandler(callback: (patientDataList: any[]) => void): void {
    if (!this.connection || this.addToWaitingListCallbacks.length === 0) return;

    this.connection.off('addToWaitingList');
    
    this.connection.on('addToWaitingList', (patientDataList: any[]) => {
      try {
        let data = patientDataList;
        if (Array.isArray(patientDataList)) {
          if (patientDataList.length === 1 && Array.isArray(patientDataList[0])) {
            data = patientDataList[0];
          }
        }
        const finalData = Array.isArray(data) ? data : [data];
        
        this.addToWaitingListCallbacks.forEach(cb => {
          try {
            cb(finalData);
          } catch (error) {
            console.error('Error in addToWaitingList callback:', error);
          }
        });
      } catch (error) {
        console.error('Error processing addToWaitingList event:', error);
      }
    });
  }

  public onAddToScheduleList(callback: (patientDataList: any[]) => void): void {
    if (!this.addToScheduleListCallbacks.includes(callback)) {
      this.addToScheduleListCallbacks.push(callback);
    }
    
    if (this.connection && this.connection.state === 'Connected') {
      this.registerAddToScheduleListHandler(callback);
    }
  }

  private registerAddToScheduleListHandler(callback: (patientDataList: any[]) => void): void {
    if (!this.connection || this.addToScheduleListCallbacks.length === 0) return;

    this.connection.off('addToScheduleList');
    this.connection.on('addToScheduleList', (patientDataList: any[]) => {
      try {
        let data = patientDataList;
        if (Array.isArray(patientDataList)) {
          if (patientDataList.length === 1 && Array.isArray(patientDataList[0])) {
            data = patientDataList[0];
          }
        }
        const finalData = Array.isArray(data) ? data : [data];
        
        this.addToScheduleListCallbacks.forEach(cb => {
          try {
            cb(finalData);
          } catch (error) {
            console.error('Error in addToScheduleList callback:', error);
          }
        });
      } catch (error) {
        console.error('Error processing addToScheduleList event:', error);
      }
    });
  }

  public onUserJoined(callback: (userData: any) => void): void {
    if (!this.userJoinedCallbacks.includes(callback)) {
      this.userJoinedCallbacks.push(callback);
    }
    
    if (this.connection && this.connection.state === 'Connected') {
      this.registerUserJoinedHandler(callback);
    }
  }

  private registerUserJoinedHandler(callback: (userData: any) => void): void {
    if (!this.connection || this.userJoinedCallbacks.length === 0) return;

    this.connection.off('UserJoined');
    this.connection.on('UserJoined', (userData: any) => {
      this.userJoinedCallbacks.forEach(cb => {
        try {
          cb(userData);
        } catch (error) {
          console.error('Error in UserJoined callback:', error);
        }
      });
    });
  }

  private registerAllHandlers(): void {
    if (!this.connection || this.connection.state !== 'Connected') return;
    
    if (this.removeFromWaitingListCallbacks.length > 0) {
      this.registerRemoveFromWaitingListHandler(this.removeFromWaitingListCallbacks[0]);
    }
    
    if (this.removeFromConsultedListCallbacks.length > 0) {
      this.registerRemoveFromConsultedListHandler(this.removeFromConsultedListCallbacks[0]);
    }
    
    if (this.removeFromScheduledListCallbacks.length > 0) {
      this.registerRemoveFromScheduledListHandler(this.removeFromScheduledListCallbacks[0]);
    }
    
    if (this.addToWaitingListCallbacks.length > 0) {
      this.registerAddToWaitingListHandler(this.addToWaitingListCallbacks[0]);
    }
    
    if (this.addToScheduleListCallbacks.length > 0) {
      this.registerAddToScheduleListHandler(this.addToScheduleListCallbacks[0]);
    }
    
    if (this.userJoinedCallbacks.length > 0) {
      this.registerUserJoinedHandler(this.userJoinedCallbacks[0]);
    }
    
    this.handlersRegistered = true;
  }

  private reregisterEventHandlers(): void {
    this.handlersRegistered = false;
    this.registerAllHandlers();
  }

  public async sendKeepAlive(): Promise<void> {
    if (!this.connection || this.connection.state !== 'Connected') {
      return;
    }

    try {
      await this.connection.invoke('SendMessage', 'system', 'ping');
    } catch (error) {
      console.warn('Keep-alive ping failed:', error);
    }
  }

  public getConnectionHealth(): {
    state: string;
    connectionId?: string;
    isConnected: boolean;
    lastError?: string;
  } {
    if (!this.connection) {
      return {
        state: 'No Connection',
        isConnected: false,
      };
    }

    return {
      state: this.connection.state,
      connectionId: this.connection.connectionId || undefined,
      isConnected: this.connection.state === 'Connected',
    };
  }

  public async testConnection(): Promise<boolean> {
    if (!this.connection || this.connection.state !== 'Connected') {
      console.warn('Cannot test connection: not connected');
      return false;
    }

    try {
      await this.connection.invoke('Ping');
      return true;
    } catch (error) {
      console.error('Connection test failed:', error);
      return false;
    }
  }
}

export const signalRService = new SignalRService();
